import { useCollection, useDocument } from "@/composables/useFirestore";
import { db } from "@/firebase";
import { useUserStore } from "@/store";
import {
  commentReaderFullPath,
  commentsFullPath,
  commentTargetKey,
  type Comment,
  type CommentReader,
  type CommentTarget,
} from "@aircap/common/comment";
import { and, collection, doc, or, orderBy, query, where } from "firebase/firestore";
import { computed, toValue, type MaybeRefOrGetter } from "vue";

/** A transfer request and its transaction share one thread: pass both as targets. */
export const useComments = (
  accountId: MaybeRefOrGetter<string>,
  targets: MaybeRefOrGetter<CommentTarget[]>,
) => {
  const { data: comments } = useCollection<Comment>(
    computed(() => {
      const thread = toValue(targets);
      if (!thread.length) return null;
      return query(
        collection<Comment>(db, commentsFullPath(toValue(accountId))),
        or(
          ...thread.map(({ targetType, targetId }) =>
            and(
              where<Comment>("targetType", "==", targetType),
              where<Comment>("targetId", "==", targetId),
            ),
          ),
        ),
        orderBy<Comment>("createdAt"),
      );
    }),
  );
  return { comments };
};

export const useUnreadComments = (accountId: MaybeRefOrGetter<string>) => {
  const user = useUserStore();
  const { data: reader } = useDocument<CommentReader>(
    computed(() =>
      user.uid ? doc(db, commentReaderFullPath(toValue(accountId), user.uid)) : null,
    ),
  );
  const unreadCount = (targets: CommentTarget[]) =>
    targets.reduce(
      (count, target) =>
        count + (reader.value?.unreadCommentIds[commentTargetKey(target)]?.length ?? 0),
      0,
    );
  return { unreadCount };
};
